// TODO: this file appears to be an exact duplicate of CreateWidgetHelper.js - DEV-4316 describes this effort.
import { isC3Experience } from "../../../shared/util/C3Utils";
define((require) => {
    "use strict";

    const Marionette       = require("marionette");
    const EditorEvents     = require("shared/services/EditorEvents");
    const WidgetTypes      = require("shared/ui/experience/widgets/WidgetTypes");
    const Promise          = require("shared/util/Promise");
    const ImageModel       = require("ui/assets/image/ImageModel");
    const $                = require("jquery");
    const _                = require("underscore");
    const GeneralFunctions = require("editor/util/GeneralFunctions");
    const ImageDropManager = require("services/ImageDropManager");
    const {Logger}         = require("shared/util/Logger");
    const MathUtil         = require("shared/util/MathUtil").default;

    const ADD_UPLOAD_BARS = "add:upload:bars";

    const WIDGET_OPTIONS = {
        "previous-scene": {
            width: 50,
            height: 50
        },
        "next-scene": {
            width: 50,
            height: 50
        },
        "text": {
            width: 250,
            disableCentering: true
        },
        "text-enhanced": {
            width: 250,
            disableCentering: true
        },
        "youtube": {
            width: 240,
            height: 135
        },
        "line": {
            height: 25
        },
        "link": {
            width: 128,
            height: 128
        },
        "html": {
            width: 128,
            height: 128
        },
        "shape": {
            width: 128,
            height: 128
        },
        "embed": {
            width: 128,
            height: 128
        },
        "dynamic-product": {
            width: 100,
            height: 100
        }
    };

    const logger = new Logger("SceneDropHandler");

    /**
     * Given a drop event, computes the offset coordinates for it.
     *
     * @param {Number} pageX - Position of the mouse pointer, relative to the left edge of the document
     * @param {Number} pageY - Position of the mouse pointer, relative to the top edge of the document.
     * @param {Object} offset - Scene Editor offset.
     * @param {Number} scale - Scene Editor scale factor.
     * @returns {Object} Coordinates, as an object with x and y properties.
     * @private
     */
    const getOffsetCoordinates = (pageX, pageY, offset, scale) => ({
        x: (pageX - offset.left) / scale,
        y: (pageY - offset.top) / scale
    });

    /**
     * Takes a widget type and some options and creates a new widget.
     *
     * @param {Object} experienceSize   The experience size.
     * @param {Object} widgetDescriptor The widget descriptor.
     * @param {Object} options          Set of options.
     * @return {AbstractWidgetModel}    The created Widget.
     * @private
     */
    const createWidget = GeneralFunctions.curry((experienceSize, widgetDescriptor, options) => {
        const widget = new widgetDescriptor.modelClass(options);

        if (_.isFunction(widgetDescriptor.initializer)) {
            widgetDescriptor.initializer(widget, experienceSize);
        }

        return widget;
    });

    /**
     * Creates a function that takes a minimal options and extends it with a
     * position and updates its width and height if they exist.
     *
     * @param {Number} experienceSize
     * @param {Object} point
     * @return {Function}
     * @private
     */
    const makeExtendOptions = (experienceSize, point) => {
        return (opt) => {
            const options = _.clone(opt);
            // Round dimensions and positioning to contain exactly required for
            // experience size values accuracy. Uses here for the dropeed widgets
            options.left = MathUtil.roundToContain((point.x / experienceSize.width * 100), experienceSize.width);
            options.top = MathUtil.roundToContain((point.y / experienceSize.height * 100), experienceSize.height);

            // convert width and height to percentage
            if (options.width) {
                options.width  = MathUtil.roundToContain(options.width / experienceSize.width * 100, experienceSize.width);
                if (!opt.disableCentering && !opt.imageId) {
                    options.left += options.width / 2;
                }
            }

            if (options.height) {
                options.height = MathUtil.roundToContain(options.height / experienceSize.height * 100, experienceSize.height);
                if (!opt.disableCentering && !opt.imageId) {
                    options.top += options.height / 2;
                }
            }
            return options;
        };
    };

    /**
     * Creates an options object based on a imageModel
     *
     * @param {Object} imageModel
     * @return {Object}
     * @private
     */
    const createImageOptions = (imageModel) => {
        const options = {
            altText:             imageModel.get("altText"),
            imageId:             imageModel.get("id"),
            height:              imageModel.get("height"),
            width:               imageModel.get("width"),
            originalImageHeight: imageModel.get("height"),
            originalImageWidth:  imageModel.get("width"),
            src:                 imageModel.get("url"),
            supportsResize:      imageModel.get("supportsResize")
        };

        if (imageModel.get("sourceType")) {
            options.sourceType = imageModel.get("sourceType");
        }

        return options;
    };

    /**
     * Takes an array of uploading ImageModels, and returns a promise that
     * resolves once all imageModels have finished.
     *
     * @param {Array} imageModels  - Array of <tt>ImageModel</tt>'s
     * @return {Promise}        - Promise that resolves with uploaded
     *                            ImageModels (failed uploads are
     *                            rejected)
     */
    const allImagesUploaded = (imageModels) => {
        let numOfFinishedUploads = 0;

        const numOfImageModels = imageModels.length;
        const deferred = $.Deferred();

        const increaseNumberOfUploads = () => {
            let uploadedModels;

            numOfFinishedUploads++;

            if (numOfFinishedUploads >= numOfImageModels) {
                uploadedModels = _.reject(imageModels, (model) => {
                    return model.get("uploadFailed");
                });

                deferred.resolve(uploadedModels);
            }
        };

        _.each(imageModels, (imageModel) => {
            imageModel.once("change:uploading change:uploadfailed", increaseNumberOfUploads);
        });

        return deferred.promise();
    };

    const getWidgetDescriptor = (dataTransfer) => {
        try {
            const data = JSON.parse(dataTransfer.getData("Text"));

            return {
                data: data,
                descriptor: WidgetTypes.lookup(data.type, true) // true = silent -> "descriptor" can be falsey.
            };
        } catch (e) { // only on parse errors
            return {};
        }
    };

    return Marionette.Controller.extend({

        _sceneEditor: null,

        _loadingRegion: null,

        _sceneLoadingScreen: null,

        /**
         * Constructor.
         *
         * @param  {Object} sceneEditor - The current <tt>SceneEditor</tt> instance.
         */
        initialize(sceneEditor) {
            this._sceneEditor = sceneEditor;
            this._loadingRegion = sceneEditor.loadingRegion;
        },

        // TODO: We need a factory for this.

        /**
         * Handles the drop event for scenes
         *
         * @param {event} event The jQuery drop event.
         * @return {Promise}
         */
        handle(event) {
            event.preventDefault();

            const originalEvent    = event.originalEvent;
            const dataTransfer     = originalEvent.dataTransfer;
            const widgetDescriptor = getWidgetDescriptor(dataTransfer); // {descriptor: WidgetType, options: *}
            const offset           = this._sceneEditor.$el.find(".widgets-boundary").offset();
            const viewModel        = this._sceneEditor.viewScope.viewModel;
            const scale            = viewModel.get("zoomModel").get("scale");
            const sceneSize        = this._sceneEditor.getSceneSize();
            const point            = getOffsetCoordinates(originalEvent.pageX, originalEvent.pageY, offset, scale);
            const extendOptions    = makeExtendOptions(sceneSize, point);

            // Dropping images from the desktop is a special case where we return
            // early with an upload descriptor containing a promise of images uploading.
            const imageModels = ImageDropManager.upload(dataTransfer).models;

            // If images were dragged frm desktop to the scene, we will create images models.
            if (imageModels.length > 0) {
                // + imageModel -> Widget
                const createWidgetFromModel = _.compose(
                    createWidget(sceneSize, WidgetTypes.IMAGE),
                    extendOptions,
                    createImageOptions
                );

                // Trigger that uploading has started, so the SceneEditor can add some uploading bars.
                if (imageModels.length > 0) {
                    this.trigger(ADD_UPLOAD_BARS, imageModels);
                    this._trackEditorEvent(imageModels);
                }

                return allImagesUploaded(imageModels)
                    .then(GeneralFunctions.map(createWidgetFromModel));
            }

            // Sanity check, bail out if we get an invalid widget type.
            if (!widgetDescriptor.descriptor) {
                logger.warn("Invalid widget type: " + dataTransfer.getData("Text"));

                return Promise.rejected("Not a widget/image");
            }

            // The next few if statements are for widget types that needs special options at
            // creation time.

            // -> Image is dropped from Library
            let options = {};


            if (WidgetTypes.IMAGE.is(widgetDescriptor.descriptor) && widgetDescriptor.data.options && !!widgetDescriptor.data.options.imageID) {
                if (isC3Experience()) {
                    options = {
                        altText: widgetDescriptor.data.options.altText,
                        imageId: widgetDescriptor.data.options.imageID,
                        height: widgetDescriptor.data.options.height,
                        width: widgetDescriptor.data.options.width,
                        originalImageHeight: widgetDescriptor.data.options.height,
                        originalImageWidth: widgetDescriptor.data.options.width,
                        src: widgetDescriptor.data.options.src
                    };
                    const widget = createWidget(sceneSize, widgetDescriptor.descriptor, extendOptions(options));
                    return Promise.resolved([widget]);
                } else {
                    return ImageModel.findByIdFromSource(widgetDescriptor.data.options.imageID, widgetDescriptor.data.options.sourceType)
                        .then((imageModel) => {
                            options = createImageOptions(imageModel);
                            const widget = createWidget(sceneSize, widgetDescriptor.descriptor, extendOptions(options));
                            return [widget];
                        });
                }
            }
            _.extend(options, WIDGET_OPTIONS[widgetDescriptor.descriptor.id], widgetDescriptor.data.options);

            // Project widgets have a default action ID and a placeholder icon.
            if (widgetDescriptor.data.defaultActionId) {
                options.defaultActionId = widgetDescriptor.data.defaultActionId;
                options.icon = widgetDescriptor.data.icon;
            }

            const widget = createWidget(sceneSize, widgetDescriptor.descriptor, extendOptions(options));

            return Promise.resolved([widget]);
        },

        /**
         * Handles the mouseDown event when the cursor contains a widget. This allows for the creation of widgets via clicking the widget icon before clicking inside the canvas.
         *
         * @param {event} event The jQuery mouseDown event.
         * @return {Promise}
         */
        handleWidgetDropFromMouseDown(event, widgetType) {
            const originalEvent = event.originalEvent;
            const descriptor = WidgetTypes.lookup(widgetType);
            if (!descriptor) {
                logger.warn("Invalid widget type: " + widgetType);
                return Promise.rejected("Not a widget/image");
            }

            const viewModel = this._sceneEditor.viewScope.viewModel;
            const offset = this._sceneEditor.$el.find(".widgets-boundary").offset();
            const scale = viewModel.get("zoomModel").get("scale");
            const sceneSize = this._sceneEditor.getSceneSize();
            const point = getOffsetCoordinates(originalEvent.pageX, originalEvent.pageY, offset, scale);
            const extendOptions = makeExtendOptions(sceneSize, point);
            const widgetOptions = WIDGET_OPTIONS[widgetType] || {};

            const widget = createWidget(this._sceneEditor.getSceneSize(), descriptor, extendOptions(widgetOptions));
            return Promise.resolved([widget]);
        },

        _trackEditorEvent(imageModels) {
            const experience = this.options.experienceModel;

            if (experience) {
                const project = this.options.project;

                const imageNames = imageModels.map((image) => {
                    return image.get("name");
                });

                const eventProperties = {
                    experienceId: experience.get("id"),
                    experienceVersion: experience.get("version"),
                    experiencePushedVersion: experience.get("pushedVersion"),
                    location: "edit",
                    numberOfUploadedImages: imageModels.length,
                    imageNames: imageNames.join(),
                    projectId: project && project.get("id"),
                    triggerOrigin: "ExperienceSceneEditorDrop"
                };

                EditorEvents.trigger(EditorEvents.constants.UPLOAD_IMAGE, eventProperties);
            }
        }

    }, {
        ADD_UPLOAD_BARS
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/SceneDropHandler.js